import { createSelector } from 'reselect';
import { Map } from 'immutable';
import { gboProfilePath } from 'paths/session';
import {
  onlineCheckInProfilePath,
  onlineCheckInProfileLoyaltyDataPath,
  onlineCheckInProfileStatusPath,
  onlineCheckInOriginPath,
  onlineCheckInProfileOciEligiblePath,
  profileFoundPath,
} from 'paths/checkin';
import { driverDetailsSelector } from 'selectors/reviewSelectors';
import { gboReservationFirstAndLastNameSelector } from 'selectors/reservationSelectors';
import { isCheckedInReservationSelector } from 'selectors/reservationFlowSelectors';
import utils from 'utils';

const getLoggedInUserProfile = (state) => state.getIn(gboProfilePath);

const getOnlineCheckInProfile = (state) => state.getIn(onlineCheckInProfilePath, Map({}));
const getOnlineCheckInProfileLoyaltyData = (state) => state.getIn(onlineCheckInProfileLoyaltyDataPath, Map({}));
const getOnlineCheckInProfileStatus = (state) => state.getIn(onlineCheckInProfileStatusPath, Map({}));
export const getOnlineCheckInProfileOciEligible = (state) => state.getIn(onlineCheckInProfileOciEligiblePath, false);
export const getProfileFound = (state) => state.getIn(profileFoundPath, false);
export const isDriverDetailsUpdated = (state) =>
  state.getIn(['gmi', 'session', 'gma', 'reservation', 'driver_details_updated'], false);
export const isNewDriveProfileFound = (state) =>
  state.getIn(['gmi', 'session', 'gma', 'reservation', 'new_driver_profile'], false);

// Returns the online check-in origin if it was provided
export const getOnlineCheckInOrigin = (state) => state.getIn(onlineCheckInOriginPath);

// Selector that determines if the current OCI profile is loyalty based on loyalty number existing
export const isLoyaltyCheckInProfileSelector = (state) => {
  const profileLoyaltyData = getOnlineCheckInProfileLoyaltyData(state);
  return !!profileLoyaltyData.get('loyalty_number');
};

// Selector that exposes whether the current user is NOT authenticated but looked up a loyalty profile
export const isUnauthUserWithLoyaltyProfileSelector = (state) => {
  const isLoyaltyCheckInProfile = isLoyaltyCheckInProfileSelector(state);
  const isUserAuthenticated = !!getLoggedInUserProfile(state);
  return isLoyaltyCheckInProfile && !isUserAuthenticated;
};

/**
 * Selector that determines if the current check-in profile is editable
 * There are two cases in which this is true - either:
 *  - the profile is non-loyalty (or non-existant)
 *  - the profile is of loyalty type and the user is authenticated to make changes
 */
export const isEditableCheckInProfileSelector = (state) => {
  const isLoyaltyCheckInProfile = isLoyaltyCheckInProfileSelector(state);
  const isUserAuthenticated = !!getLoggedInUserProfile(state);
  return !isLoyaltyCheckInProfile || (isLoyaltyCheckInProfile && isUserAuthenticated);
};

// Selector that parses different online check-in profile completion status
export const onlineCheckInStatusSelector = (state) => {
  const onlineCheckInProfileStatus = getOnlineCheckInProfileStatus(state);
  const driverProfileComplete = !!onlineCheckInProfileStatus.get('driverlicense_details_present');
  const addressComplete = !!onlineCheckInProfileStatus.get('address_details_present');
  const contactComplete = !!onlineCheckInProfileStatus.get('contact_details_present');
  const ociComplete = getOnlineCheckInProfileOciEligible(state);

  return {
    driverProfileComplete,
    addressComplete,
    contactComplete,
    contactProfileComplete: addressComplete && contactComplete,
    ociComplete,
  };
};

// Selector that checks if there is a partial profile
export const partialProfileSelector = createSelector(
  [onlineCheckInStatusSelector, getProfileFound],
  ({ driverProfileComplete, contactProfileComplete }, profileFound) =>
    (!driverProfileComplete || !contactProfileComplete) && profileFound
);

// Selector that exposes whether the current user is NOT authenticated and has a partial loyalty profile
export const isUnauthPartialProfileSelector = (state) => {
  const isPartialProfileFound = partialProfileSelector(state);
  const isUserAuthenticated = !!getLoggedInUserProfile(state);
  return isPartialProfileFound && !isUserAuthenticated;
};

// Selector that parses the current step config based on the check-in origin + current step hash
export const stepConfigSelector = (state) => {
  const onlineCheckInProfile = getOnlineCheckInProfile(state);
  const isProfileFound = !!onlineCheckInProfile?.getIn(['profile', 'individual_id']);
  const isOCIProfileLoyalty = isLoyaltyCheckInProfileSelector(state);
  const { ociComplete: isOCIProfileComplete } = onlineCheckInStatusSelector(state);

  const authenticatedUserProfile = getLoggedInUserProfile(state);
  const isUserAuthenticated = !!authenticatedUserProfile;
  const authUserFirstName = authenticatedUserProfile?.getIn(['profile', 'basic_profile', 'first_name']);

  // We want to take the hash, but remove the initial character, so it matches the format `/step`
  const currentStepHash = utils.url.getCurrentPageHash();
  const onlineCheckInOrigin = getOnlineCheckInOrigin(state) || '';

  const currentStepContent = utils.checkin.getStepsContent(currentStepHash, authUserFirstName, {
    isProfileFound,
    isOCIProfileComplete,
    isOCIProfileLoyalty,
    isUserAuthenticated,
  });
  const stepCounterData = utils.checkin.parseStepCounter(currentStepHash, onlineCheckInOrigin);

  return { ...currentStepContent, stepCounter: stepCounterData };
};

export const isLastStepSelector = createSelector(
  [stepConfigSelector],
  (stepConfig) => stepConfig.stepCounter.current === stepConfig.stepCounter.total
);

const onlineCheckInProfileCopyEligibility = createSelector([getOnlineCheckInProfile], (profileData) => ({
  contact:
    profileData.getIn(['eligible_for_copy_profile', 'copy_address_profile'], false) &&
    profileData.getIn(['eligible_for_copy_profile', 'copy_emails'], false) &&
    profileData.getIn(['eligible_for_copy_profile', 'copy_phones'], false),
  license: profileData.getIn(['eligible_for_copy_profile', 'copy_license_profile'], false),
}));

// Selector that parses online check-in profile fields
export const onlineCheckInProfileSelector = createSelector(
  [getOnlineCheckInProfile, onlineCheckInProfileCopyEligibility, getProfileFound],
  (profileData, profileCopyElibility, isProfileFound) => ({
    individual_id: profileData.getIn(['profile', 'individual_id']),
    basic_profile: {
      first_name: profileData.getIn(['profile', 'basic_profile', 'first_name']),
      last_name: profileData.getIn(['profile', 'basic_profile', 'last_name']),
      birth_date: profileData.getIn(['profile', 'basic_profile', 'birth_date']),
    },
    license_profile: {
      country_code: profileData.getIn(['license_profile', 'country_code']),
      country_name: profileData.getIn(['license_profile', 'country_name']),
      country_subdivision_code: profileData.getIn(['license_profile', 'country_subdivision_code']),
      issuing_authority: profileData.getIn(['license_profile', 'issuing_authority']),
      license_number: profileData.getIn(['license_profile', 'license_number']),
      license_expiration_date: profileData.getIn(['license_profile', 'license_expiration_date']),
      license_issue_date: profileData.getIn(['license_profile', 'license_issue_date']),
      birth_date: profileData.getIn(['license_profile', 'birth_date']),
    },
    address_profile: {
      street_addresses: utils.safeToJS(profileData.getIn(['address_profile', 'street_addresses']), []),
      country_name: profileData.getIn(['address_profile', 'country_name']),
      country_code: profileData.getIn(['address_profile', 'country_code']),
      country_subdivision_code: profileData.getIn(['address_profile', 'country_subdivision_code']),
      city: profileData.getIn(['address_profile', 'city']),
      postal: profileData.getIn(['address_profile', 'postal']),
    },
    contact_profile: {
      phones: utils.safeToJS(profileData.getIn(['contact_profile', 'phones']), []),
      email: profileData.getIn(['contact_profile', 'email'], ''),
    },
    copy_eligible: profileData.hasIn(['eligible_for_copy_profile']),
    eligible_for_copy: profileCopyElibility,
    loyalty_data: utils.safeToJS(profileData.getIn(['profile', 'basic_profile', 'loyalty_data'], {})),
    profile_found: isProfileFound,
  })
);

export const onlineCheckInProfileNameSelector = createSelector([getOnlineCheckInProfile], (profileData) => ({
  ociFirstName: profileData.getIn(['profile', 'basic_profile', 'first_name']),
  ociLastName: profileData.getIn(['profile', 'basic_profile', 'last_name']),
}));

export const getHasOnlineCheckInProfile = (state) => state.hasIn(onlineCheckInProfilePath);

export const isResFirstNameDifferentThanOciProfileSelector = createSelector(
  [getProfileFound, gboReservationFirstAndLastNameSelector, onlineCheckInProfileNameSelector],
  (isProfileFound, resInfo, ociInfo) =>
    isProfileFound &&
    resInfo.resFirstName?.toUpperCase() !== ociInfo.ociFirstName?.toUpperCase() &&
    resInfo.resLastName?.toUpperCase() === ociInfo.ociLastName?.toUpperCase()
);

export const isReservationWithEqualLastNameAndDifferentFirstName = createSelector(
  [getLoggedInUserProfile, gboReservationFirstAndLastNameSelector],
  (authenticatedUserProfile, resInfo) => {
    const isUserAuthenticated = !!authenticatedUserProfile;
    const authUserFirstName = authenticatedUserProfile?.getIn(['profile', 'basic_profile', 'first_name']);
    const authUserLastName = authenticatedUserProfile?.getIn(['profile', 'basic_profile', 'last_name']);

    return (
      isUserAuthenticated &&
      resInfo.resFirstName?.toUpperCase() !== authUserFirstName &&
      resInfo.resLastName?.toUpperCase() === authUserLastName
    );
  }
);

export const showActivatedProfileInResFlow = createSelector(
  [
    getProfileFound,
    onlineCheckInStatusSelector,
    getLoggedInUserProfile,
    isDriverDetailsUpdated,
    driverDetailsSelector,
    isNewDriveProfileFound,
    isCheckedInReservationSelector,
  ],
  (
    isProfileFound,
    onlineCheckInStatus,
    isUserAuthenticated,
    driverDetailsUpdated,
    driverDetails,
    newDriverProfile,
    isCheckedInReservation
  ) => {
    let status = false;
    const { driverProfileComplete, contactProfileComplete, ociComplete } = onlineCheckInStatus;
    const isAuthenticatedCompletedProfile = isUserAuthenticated && driverProfileComplete && contactProfileComplete;
    const foundCompleteProfile = driverProfileComplete && contactProfileComplete && ociComplete;

    // Checking for authenticated user or reservation_sub_status is CHECKED_IN, if it is true then showing an OCI completed profile
    if (isAuthenticatedCompletedProfile || isCheckedInReservation) {
      status = true;
    } else if (
      foundCompleteProfile &&
      driverDetails?.reservationFullName &&
      ((!isProfileFound && newDriverProfile) || (isProfileFound && driverDetailsUpdated) || isProfileFound)
    ) {
      status = true;
    }
    return status;
  }
);
